import React, { useEffect, useState } from "react";

import momentTZ from "moment-timezone";
import { useIntl } from "react-intl";

import EnvironmentImpact from "Containers/EnvironmentImpact";
import { Button, ButtonWrapper } from "ds/Button";
import Dialog from "ds/Dialog";
import Select from "ds/Select";
import useSelectorWithUrlParams from "Hooks/useSelectorWithUrlParams";
import { capitalize } from "Libs/utils";
import { getIsLoadingProjects } from "Reducers/project/project";
import { getRegions, regionLabelSelector } from "Reducers/project/region";
import { useAppDispatch, useAppSelector } from "Store/hooks";

import * as S from "./ProjectGeneralSettingsForm.styles";

import type { Project } from "@packages/client";
import type { GeneralProjectFormDataType } from "src/organization/pages/project/settings/ProjectGeneralSettings/type";

const timezoneList = momentTZ.tz.names().map(tz => {
  return { label: tz, value: tz };
});

type ProjectBasicFormProps = {
  onSave: (data: GeneralProjectFormDataType) => void;
  project: Project;
};

const ProjectBasicForm = ({ onSave, project }: ProjectBasicFormProps) => {
  const intl = useIntl();
  const dispatch = useAppDispatch();

  const [name, setName] = useState(project?.title);
  const [timezone, setTimezone] = useState(project?.timezone);
  const [isChanged, setIsChanged] = useState(false);
  const loading = useAppSelector(getIsLoadingProjects);

  const regionLabel = useSelectorWithUrlParams(regionLabelSelector);

  useEffect(() => {
    dispatch(getRegions());
  }, [dispatch]);

  const localSave = e => {
    e.preventDefault();
    onSave({ timezone, title: name });
    setIsChanged(false);
  };

  const onNameChange = e => {
    const newName = e.target.value;
    setName(newName);
    setIsChanged(Boolean(newName));
  };

  const onTimezoneChange = tz => {
    setTimezone(tz.value);
    setIsChanged(true);
  };

  const cancel = () => {
    setName(project?.title);
    setTimezone(project?.timezone);
    setIsChanged(false);
  };

  return (
    <S.Form aria-label="Project settings">
      <S.FormFields>
        <S.Input
          onChange={onNameChange}
          id="project-name"
          label={intl.formatMessage({
            id: "project.settings.basic.form.name",
            defaultMessage: "Project name"
          })}
          placeholder={intl.formatMessage({
            id: "project.settings.basic.form.name",
            defaultMessage: "Project name"
          })}
          analyticId="project.settings.basic.form.name"
          value={name}
          required
          disabled={loading}
        />

        <S.Input
          onChange={() => undefined}
          id="project-region"
          analyticId="project.settings.basic.form.region"
          label={intl.formatMessage({
            id: "project.settings.basic.form.region",
            defaultMessage: "Region"
          })}
          value={`${regionLabel.title} ${regionLabel.suffix}`}
          disabled
        />
        <S.Region>
          <EnvironmentImpact
            regionId={project.region}
            shouldShowAllLeafs={false}
          />
        </S.Region>

        <S.SelectWrapper>
          <S.LabelWrapper>
            <S.Label htmlFor="project-general-setting-timezone" required>
              {intl.formatMessage({
                id: "project.settings.basic.form.timezone.label",
                defaultMessage: "Timezone"
              })}
            </S.Label>
            <Dialog
              analyticId="project.settings.basic.form.timezone.info"
              description={intl.formatMessage({
                id: "project.settings.basic.form.timezone.info",
                defaultMessage:
                  "The project timezone is used to schedule backups."
              })}
            />
          </S.LabelWrapper>

          <Select
            id="project-general-setting-timezone"
            options={timezoneList}
            value={{
              label: timezone,
              value: timezone
            }}
            onChange={onTimezoneChange}
            placeholder={intl.formatMessage({
              id: "project.settings.basic.form.timezone.placeholder",
              defaultMessage: "Select timezone"
            })}
            required
            isDisabled={loading}
          />
        </S.SelectWrapper>
      </S.FormFields>
      {loading ? (
        <S.Loading />
      ) : (
        isChanged && (
          <ButtonWrapper spacing="modal">
            <Button
              analyticId="save"
              id="save_project_general_settings_button"
              type="submit"
              onClick={localSave}
            >
              {capitalize(intl.formatMessage({ id: "save" }))}
            </Button>
            <Button
              analyticId="cancel"
              id="cancel-project-general-btn"
              variant="secondary"
              onClick={cancel}
            >
              {capitalize(intl.formatMessage({ id: "cancel" }))}
            </Button>
          </ButtonWrapper>
        )
      )}
    </S.Form>
  );
};

export default ProjectBasicForm;
